import React from 'react';
import { contentfulApi } from '..';
import { BLOG_INDEX_PAGE_LIMIT, BLOG_CATEGORIES } from '../../constants';
import { IBlogCategory } from '../../interfaces/IBlogCategory';
import {
  IBlogReducerStateData,
  IBlogReducerStatePaginationData,
} from '../../interfaces/IBlogReducer';
import { TBlogReducerAction } from '../reducers/blog-reducer';

interface IBlogPostsQuery {
  pageNumber: number;
  activeCategory: IBlogCategory;
  categoriesData: IBlogReducerStateData;
  paginationData: IBlogReducerStatePaginationData;
  dispatch: React.Dispatch<TBlogReducerAction>;
  keyword: string;
}

const fragmentBlogPostCollection = `
fragment BlogPostCollectionFragment on BlogPostCollection {
  limit
  skip
  total
  items {
    title
    slug
    categories
    publishDate
    heroImage {
      url
    }
    description
  }
}
`;

const queryByKeyword = `
  query BlogByCategory($limit: Int = 6, $skip: Int, $keyword: String, $preview: Boolean = false) {
    blogPostCollection(limit: $limit, skip: $skip, preview: $preview, order: publishDate_DESC, where:{title_contains:$keyword}) {
      ...BlogPostCollectionFragment
    }
  }
`;

const queryByCategory = `
  query BlogByCategory($category: String!, $limit: Int= 6, $skip: Int, $preview: Boolean = false) {
    blogPostCollection(where: {categories_contains_some: [$category]}, limit: $limit, skip: $skip, preview: $preview, order:publishDate_DESC) {
      ...BlogPostCollectionFragment
    }
  }
`;

const queryByAllCategory = `
  query BlogByCategory($limit: Int= 6, $skip: Int, $preview: Boolean = false) {
    blogPostCollection(limit: $limit, skip: $skip, preview: $preview, order:publishDate_DESC) {
      ...BlogPostCollectionFragment
    }
  }
`;

interface ResponseType {
  data: {
    blogPostCollection: {
      items: [];
      limit: number;
      skip: number;
      total: number;
    };
  };
}

interface IGetQueryVariables {
  pageNumber: number;
  activeCategory: IBlogCategory;
  keyword: string;
}

const getQueryVariables = ({
  pageNumber,
  activeCategory,
  keyword,
}: IGetQueryVariables): object | null => {
  if (activeCategory === BLOG_CATEGORIES.find(item => item.key === 'all')) {
    return {
      limit: BLOG_INDEX_PAGE_LIMIT,
      skip: (pageNumber - 1) * BLOG_INDEX_PAGE_LIMIT,
    };
  }
  if (activeCategory === BLOG_CATEGORIES.find(item => item.key === 'search')) {
    return {
      keyword,
      limit: BLOG_INDEX_PAGE_LIMIT,
      skip: (pageNumber - 1) * BLOG_INDEX_PAGE_LIMIT,
    };
  }
  return {
    category: activeCategory.value,
    limit: BLOG_INDEX_PAGE_LIMIT,
    skip: (pageNumber - 1) * BLOG_INDEX_PAGE_LIMIT,
  };
};

const getQuery = (activeCategory: IBlogCategory) => {
  if (activeCategory === BLOG_CATEGORIES.find(item => item.key === 'all')) {
    return queryByAllCategory + fragmentBlogPostCollection;
  }
  if (activeCategory === BLOG_CATEGORIES.find(item => item.key === 'search')) {
    return queryByKeyword + fragmentBlogPostCollection;
  }
  return queryByCategory + fragmentBlogPostCollection;
};

const blogPostsQuery = ({
  pageNumber,
  activeCategory,
  categoriesData,
  paginationData,
  dispatch,
  keyword,
}: IBlogPostsQuery) => {
  const categoriesDataTemp = categoriesData;
  const paginationDataTemp = paginationData;
  dispatch({
    type: 'request',
  });
  contentfulApi({
    query: getQuery(activeCategory),
    variables: getQueryVariables({ pageNumber, activeCategory, keyword }),
  }).then(
    (results: ResponseType) => {
      const { items, limit, total } = results.data.blogPostCollection;

      if (!categoriesDataTemp[activeCategory.value]) {
        categoriesDataTemp[activeCategory.value] = {};
      }
      categoriesDataTemp[activeCategory.value][pageNumber] = items;
      paginationDataTemp[activeCategory.value] = {
        limit,
        skip: pageNumber * BLOG_INDEX_PAGE_LIMIT,
        total,
      };

      dispatch({
        type: 'success',
        data: categoriesDataTemp,
        paginationData: paginationDataTemp,
      });
    },
    (err: string) => dispatch({ type: 'failure', error: err }),
  );
};

export default blogPostsQuery;
